import cloneDeep from "lodash/cloneDeep";
import find from "lodash/find";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import pullAt from "lodash/pullAt";
import set from "lodash/set";
import sortBy from "lodash/sortBy";
import uniq from "lodash/uniq";
import { PARAMETER_OPTIONS, PARAMETER_OPTIONS_ARRAY } from "../constants";
import i18n from "../i18n";
import getErrorMessage from "../utils/getErrorMessage";
import resetForm from "../utils/reset-form";
import {
  isLibraryMetadataFromAddExercise,
  respondLibraryMetadataFromAddExercise,
} from "./respond-library-metadata-from-add-exercise";

const { FREEFORM } = PARAMETER_OPTIONS;

import { buildLibraryArray } from "../utils/exercise-catalog";
import {
  filterAthletesAttrs,
  formatProfileItem,
  syncAthleteData,
  whoIsAthleteCoach,
} from "../utils/user-helpers";

const cleanAndSortParameters = (parameters) => {
  return PARAMETER_OPTIONS_ARRAY.filter((elem) => parameters.includes(elem));
};

const otherExerciseProperties = async (
  body,
  files,
  api,
  assert,
  user,
  exercise = null
) => {
  const {
    parameter0,
    parameter1,
    parameter2,
    parameter3,
    exerciseTypeField,
    primaryMuscleGroup,
    secondaryMuscleGroup,
    instructions,
    videoUrl,
    videoDeleteOtherExercise,
  } = body;

  const parameters = cleanAndSortParameters([
    parameter0,
    parameter1,
    parameter2,
    parameter3,
  ]);

  let videoData;
  if (!videoUrl && !isEmpty(files)) {
    videoData = await (
      await api("other-exercise-video", {
        method: "POST",
        body: { userId: user._id, userEmail: user.email },
        files,
      })
    ).json();
  } else if (videoUrl || exercise?.video?.url) {
    videoData = {
      url: videoUrl || exercise.video?.url,
    };
  }

  return {
    parameters: parameters[0] === FREEFORM ? [parameters[0]] : parameters,
    video: videoData || (videoDeleteOtherExercise ? "" : exercise?.video || ""), // video empty it means deleted
    exerciseTypeField: exerciseTypeField === "None" ? "" : exerciseTypeField,
    primaryMuscleGroup: primaryMuscleGroup === "None" ? "" : primaryMuscleGroup,
    secondaryMuscleGroup:
      secondaryMuscleGroup === "None" ? "" : secondaryMuscleGroup,
    instructions: instructions ? instructions.trim() : "",
  };
};

const isSameMovement = (first, second) =>
  String(first).toLowerCase() === String(second).toLowerCase();

const User = (app) => {
  app
    // Add an exercise to the users library.
    .post(
      "/user/add-secondary",
      async ({ req, res, api, locals, assert, load, flash }) => {
        const { body, files } = req;

        const { query, activeUserToRequest } = locals;
        const { forAthlete } = query;

        if (forAthlete) {
          await load("profileUser", { user: forAthlete });
        }

        const user = locals.profileUser || activeUserToRequest;

        // Work on a copy: the user object is only updated once the API accepts
        // the change, otherwise a rejected save would still show after reload.
        let library = cloneDeep(
          get(user, `exerciseLibrary.${body.primary}`, [])
        );

        assert(body.movement, 400, i18n.t("auth:user.exerciseNameRequired"));
        if (body.primary !== "assistant")
          assert(body.repMaximum, 400, i18n.t("auth:user.repMaxRequired"));

        // Case-insensitive, like the API's duplicate check.
        const exists = find(library, ({ movement }) =>
          isSameMovement(movement, body.movement)
        );

        assert(!exists, 400, i18n.t("auth:user.exerciseAlreadyInLibrary"));
        const otherExtraProperties = await otherExerciseProperties(
          body,
          files,
          api,
          assert,
          user
        );

        const newExercise = {
          movement: body.movement,
          repMaximum: body.repMaximum || null,
          customizedRM: true,
          ...otherExtraProperties,
        };
        library.push(newExercise);

        library = sortBy(library, [
          (secondary) => String(secondary.movement).toLowerCase(),
        ]);

        await api(`exercise-library-secondary/${user._id}`, {
          method: "POST",
          ...(forAthlete ? { query: { proxyAthlete: forAthlete } } : {}),
          body: {
            primary: body.primary,
            exercise: newExercise,
          },
        });

        set(user, `exerciseLibrary.${body.primary}`, library);
        flash("success", i18n.t("auth:user.addedSuccess"));

        //commented this out teporarily; to ensure Cole is aware
        const refreshOpts = {};
        if (body.source === "settings") refreshOpts.modal = "";
        else refreshOpts.secondaryAdded = true;
        res.refreshWith(refreshOpts, { url: "back", hash: "#_" });
      }
    )

    .post(
      "/user/edit-secondary/:index",
      async ({ req, res, api, locals, assert, load, flash }) => {
        const { params, body, files } = req;
        const { query, activeUserToRequest } = locals;
        const { forAthlete } = query;
        if (forAthlete) {
          await load("profileUser", { user: forAthlete });
        }
        const user = locals.profileUser || activeUserToRequest;
        const fromAddExercise = isLibraryMetadataFromAddExercise(body.source);
        // Sorting the library before editing was causing the wrong movement to be picked
        // on unsorted libraries (e.g. brand new accounts)
        let library = cloneDeep(
          get(user, `exerciseLibrary.${body.primary}`, [])
        );

        assert(
          library[params.index],
          400,
          i18n.t("auth:user.customMovementDeleted")
        );
        assert(body.movement, 400, i18n.t("auth:user.exerciseNameRequired2"));
        if (body.primary !== "assistant")
          assert(body.repMaximum, 400, i18n.t("auth:user.repMaxRequired2"));

        const otherExtraProperties = await otherExerciseProperties(
          body,
          files,
          api,
          assert,
          user,
          library[params.index]
        );

        const movementBeingUpdated = { ...library[params.index] };

        const updatedExercise = {
          movement: body.movement,
          repMaximum: body.repMaximum || null,
          customizedRM: true,
          ...otherExtraProperties,
        };
        library[params.index] = updatedExercise;

        const exists = library.filter((item) =>
          isSameMovement(item.movement, body.movement)
        );

        assert(
          exists?.length <= 1,
          400,
          i18n.t("auth:user.exerciseAlreadyExists")
        );

        library = sortBy(library, [
          (secondary) => String(secondary.movement).toLowerCase(),
        ]);

        delete body.source;

        const exerciseForApi = { ...updatedExercise };
        //if before there was a video link and in the body the link has been deleted
        if (exerciseForApi.video?.url && body.videoUrl?.length === 0) {
          exerciseForApi.video = { ...exerciseForApi.video, url: "" };
        }

        await api(`exercise-library-secondary/${user._id}`, {
          method: "PUT",
          ...(forAthlete ? { query: { proxyAthlete: forAthlete } } : {}),
          body: {
            primary: body.primary,
            movement: movementBeingUpdated.movement,
            videoDeleteOtherExercise: body.videoDeleteOtherExercise,
            exercise: exerciseForApi,
          },
        });

        // After the API call: a rejected save must not leave the unsaved
        // change on the user object (it would show after reload).
        set(user, `exerciseLibrary.${body.primary}`, library);

        if (body.primary !== "assistant") {
          if (
            movementBeingUpdated.repMaximum !== body.repMaximum &&
            confirm(i18n.t("auth:user.applyNewRmConfirm"))
          ) {
            await api(`user/${user._id}`, {
              method: "PUT",
              body: {
                action: "updateFutureRM",
                type: "secondary",
                primary: body.primary,
                secondary: body.movement,
                secondaryRepMaximum: body.repMaximum,
              },
            });
          }
        }

        api(`update-cycle-exercises-metadata`, {
          method: "PUT",
          body: {
            email: user.email,
            primary: body.primary,
            movement: body.movement,
            prevMovement: movementBeingUpdated.movement,
          },
        });

        if (fromAddExercise) {
          return respondLibraryMetadataFromAddExercise(
            res,
            otherExtraProperties
          );
        }

        flash("success", i18n.t("auth:user.updatedSuccess"));
        res.refreshWith({ modal: "" }, { url: "back" });
      }
    )
    .get(
      "/user/remove-secondary/:primary/:index",
      async ({ req, res, api, locals, load, flash }) => {
        const { params, query } = req;
        const { activeUserToRequest } = locals;
        const { forAthlete } = query;

        if (forAthlete) {
          await load("profileUser", { user: forAthlete });
        }
        const user = locals.profileUser || activeUserToRequest;

        const library = uniq(
          get(user, `exerciseLibrary.${params.primary}`) || []
        );
        const toRemove = library[params.index];
        if (!toRemove) {
          flash("error", i18n.t("auth:user.customMovementDeleted"));
          return res.backDontScroll();
        }

        pullAt(library, params.index);

        set(
          user,
          `exerciseLibrary.${params.primary}`,
          isEmpty(library) ? [] : library
        );

        /**
         * If the exercise deleted was the last one of the library we are going to delete that property
         * from the exercise library
         */
        if (user.exerciseLibrary && typeof user.exerciseLibrary === "object") {
          Object.keys(user.exerciseLibrary).forEach((key) => {
            if (
              Array.isArray(user.exerciseLibrary[key]) &&
              isEmpty(user.exerciseLibrary[key])
            ) {
              delete user.exerciseLibrary[key];
            }
          });
        }

        try {
          await api(`exercise-library-secondary/${user._id}`, {
            method: "DELETE",
            query: {
              primary: params.primary,
              movement: toRemove.movement,
              ...(forAthlete ? { proxyAthlete: forAthlete } : {}),
            },
          });
          flash("success", i18n.t("auth:user.deletedSuccess"));
        } catch (e) {
          flash("error", getErrorMessage(e));
        }

        res.backDontScroll();
      }
    )
    .get(
      "/user/update-email/:token",
      async ({ req: { params }, res, api, flash }) => {
        await (
          await api("update-email", {
            method: "POST",
            body: { token: params.token },
          })
        ).json();
        flash("success", i18n.t("auth:user.emailChangedSuccess"));
        res.redirect("/dashboard");
      }
    )
    .post(
      "/user/reset-email",
      async ({ req, locals, res, api, flash, assert }) => {
        const { body } = req;

        assert(body.email, 400, i18n.t("auth:user.newEmailRequired"));
        assert(
          String(body.email).toLowerCase() !== locals.activeUser.email,
          400,
          i18n.t("auth:user.differentEmailRequired")
        );
        assert(
          body.currentPassword,
          400,
          i18n.t("auth:user.currentPasswordRequired")
        );

        await api("change-email", { method: "POST", body });
        flash("success", i18n.t("auth:user.emailSentVerify"));
        res.backDontScroll();
      }
    )
    .post("/user/change-password", async ({ req, res, api, flash, assert }) => {
      try {
        const { body } = req;

        assert(
          body.currentPassword,
          400,
          i18n.t("auth:user.currentPasswordRequired2")
        );
        assert(body.password, 400, i18n.t("auth:user.newPasswordRequired"));
        assert(
          body.confirmPassword,
          400,
          i18n.t("auth:user.confirmPasswordRequired")
        );
        assert(
          body.confirmPassword === body.password,
          400,
          i18n.t("auth:user.passwordsMustMatch")
        );

        await api(`user-change-password`, { method: "PUT", body }).then(
          (response) => {
            if (response.status === 200) {
              flash("success", i18n.t("auth:user.updatedSuccess"));
              resetForm("#form-change-password");
              res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
            } else {
              flash(
                "error",
                res.statusText || i18n.t("auth:user.anErrorOccurred")
              );
              res.refreshWith({}, { url: "back", hash: "#_" });
            }
          }
        );
      } catch (e) {
        flash("error", e.message);
        res.refreshWith({}, { url: "back", hash: "#_" });
      }
    })
    .post(
      "/user/update-password",
      async ({ req: { body }, res, api, flash }) => {
        await (
          await api("update-password", {
            method: "POST",
            body,
          })
        ).json();
        flash("success", i18n.t("auth:user.passwordUpdatedSuccess"));
        res.redirect("/login");
      }
    )

    .post(
      "/user/reset-password",
      async ({ req: { body }, res, api, flash }) => {
        await api("change-password", { method: "POST", body });
        flash("success", i18n.t("auth:user.resetSentSuccess"));
        res.backDontScroll();
      }
    )
    .post(
      "/user/update-notifications",
      async ({ req: { body }, res, locals, api, flash }) => {
        const { activeUser } = locals;
        if (isEmpty(body.notifications)) body.notifications = null;
        body.action = "settings";
        await api(`user/${activeUser._id}`, { method: "PUT", body });
        flash("success", i18n.t("auth:user.updatedSuccess"));
        res.backDontScroll();
      }
    )
    .post(
      "/user/user-denied-signup",
      async ({ req: { body }, res, assert, locals, api, flash }) => {
        const { activeUser } = locals;
        body.user = activeUser;

        assert(body.whyNotSignup, 400, i18n.t("auth:user.selectOneCheckbox"));

        await api(`feedback`, { method: "POST", body });
        flash("success", i18n.t("auth:user.feedbackReceived"));
        res.redirect("/logout");
      }
    )
    .post(
      "/user/cancel-account",
      async ({ req, res, locals, api, fail, flash, assert, logout }) => {
        assert(req.body.password, 400, i18n.t("auth:user.passwordRequired"));
        try {
          await api(`user-cancel-subscription`, {
            method: "PUT",
            body: req.body,
          });
          flash("success", i18n.t("auth:user.subscriptionCancelled"));
          res.redirect("/dashboard/settings/subscription");
        } catch (e) {
          flash("error", e.message);
          const modal = get(req.query, "modal", {});
          res.redirect(
            "/dashboard/settings/subscription?modal%5Btype%5D=cancel"
          );
        }
      }
    )
    .post(
      "/user/update-permission-plan",
      async ({ req: { body }, res, api, flash, assert }) => {
        try {
          assert(body.userId, 400, i18n.t("auth:user.specifyUserToUpdate"));
          if (!body.plan) {
            body.plan = null;

            if (
              body.askForCreditCard === "false" &&
              body.permissions?.includes("coach")
            ) {
              await api("register-coach", {
                method: "POST",
                body: { existingEmail: body.email },
              });
            }
          }
          delete body.email;
          await api("update-permission-plan", { method: "PUT", body });

          flash("success", i18n.t("auth:user.permissionUpdated"));
          res.refreshWith({ modal: "", user: "" }, { url: "back", hash: "#_" });
        } catch (err) {
          flash("error", err.message);
        }
      }
    )
    .post("/user/athlete-video-shown", async ({ api, flash }) => {
      try {
        await api("athlete-video-shown", { method: "PUT", body: {} });
      } catch (err) {
        flash("error", err.message);
      }
    })
    .post("/user/video-shown", async ({ req: { body }, res, api, flash }) => {
      try {
        await api("video-shown", {
          method: "PUT",
          body,
        });
        res.status = 204;
      } catch (err) {
        flash("error", err.message);
      }
    })
    // A coach stops training an athlete
    .post(
      "/user/stop-training/:athleteId",
      async ({ req: { body, params }, res, locals, api, flash, assert }) => {
        assert(
          params.athleteId,
          400,
          i18n.t("auth:user.specifyAthleteToUpdate")
        );
        const { activeUser, organizationRoleName } = locals;
        const { athleteId } = params;

        // Nutritionist can't stop training athletes
        assert(
          organizationRoleName !== "Nutritionist",
          400,
          i18n.t("auth:user.noPermission")
        );

        // Check if user acknowledged all the points
        assert(
          body.acknowledge?.length === 5,
          400,
          i18n.t("auth:user.acknowledgeAllPoints")
        );

        const userToRequest =
          !!body.coachOrganizationInformation &&
          String(
            whoIsAthleteCoach(
              activeUser.athletes,
              athleteId,
              activeUser._id,
              JSON.parse(body.coachOrganizationInformation)._id
            )
          ) !== String(activeUser._id)
            ? JSON.parse(body.coachOrganizationInformation)
            : activeUser;

        delete body.coachOrganizationInformation;

        // Find athlete to change
        const athleteToChange = find(userToRequest.athletes, {
          _id: athleteId,
        });

        assert(
          !athleteToChange.isGymMember,
          400,
          "Cannot stop training a PRESS user"
        );

        // Prepare API requests
        // Update the athlete user (delete the coach)
        const updateAthleteRequest = api(`user/${athleteId}`, {
          method: "PUT",
          body: {
            coach: { dummy: true },
            proxyAthlete: athleteId,
          },
        });
        // Update the coach user — atomic single-athlete write, so it can't
        // clobber an athlete added to the roster after this page loaded
        // (sc-45101).
        const updateCoachAthletesRequest = api(
          `update-coach-athlete-status/${userToRequest._id}`,
          {
            method: "PUT",
            body: {
              athleteId,
              inactive: true,
              inactiveReason: "Coach stopped training",
            },
          }
        );

        const stopBillingRequest = api("stop-billing-coach-for-athlete", {
          method: "PUT",
          query: {
            athlete: athleteId,
            userBillingId: locals.activeUserToRequest._id,
            coachId: userToRequest._id,
          },
        });

        try {
          // Execute all requests concurrently
          await Promise.all([
            updateAthleteRequest,
            updateCoachAthletesRequest,
            stopBillingRequest,
          ]);

          flash(
            "success",
            i18n.t("auth:user.stoppedTraining", {
              name: athleteToChange.name,
            })
          );
          res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
        } catch (error) {
          console.error("Error stopping training:", error);
          flash("error", i18n.t("auth:user.stopTrainingError"));
          res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
        }
      }
    )
    // A coach deletes an athlete from their list of athletes
    .post(
      "/user/remove-athlete/:athleteId",
      async ({ req: { body, params }, res, locals, api, flash, assert }) => {
        assert(
          params.athleteId,
          400,
          i18n.t("auth:user.specifyAthleteToUpdate")
        );
        const { activeUser, organizationRoleName } = locals;
        const { athleteId } = params;
        const referAsClients = Boolean(activeUser.referAsClients);

        // Nutritionist can't stop training athletes
        assert(
          organizationRoleName !== "Nutritionist",
          400,
          i18n.t("auth:user.noPermission")
        );

        // Check if user acknowledged all points
        assert(
          body.acknowledge.length === 3,
          400,
          i18n.t("auth:user.acknowledgeAllPoints")
        );

        const userToRequest =
          !!body.coachOrganizationInformation &&
          String(
            whoIsAthleteCoach(
              activeUser.athletes,
              athleteId,
              activeUser._id,
              JSON.parse(body.coachOrganizationInformation)._id
            )
          ) !== String(activeUser._id)
            ? JSON.parse(body.coachOrganizationInformation)
            : activeUser;

        delete body.coachOrganizationInformation;

        // Find athlete to delete
        const athleteToChange = find(userToRequest.athletes, {
          _id: athleteId,
        });

        assert(
          !athleteToChange.isGymMember,
          400,
          i18n.t("auth:user.cannotRemovePressUser")
        );

        // Update coach — atomic $pull, so it can't clobber an athlete added
        // to the roster after this page loaded (sc-45101).
        await api(`remove-coach-athlete/${userToRequest._id}`, {
          method: "PUT",
          body: { athleteId },
        });

        // Update Athlete
        const athlete = JSON.parse(body.athlete);
        if (!athlete.avatar) delete athlete.avatar;
        athlete.coach = { dummy: true };
        athlete.proxyAthlete = athleteId;
        await api(`user/${athleteId}`, {
          method: "PUT",
          body: athlete,
        });

        flash(
          "success",
          i18n.t("auth:user.removedFromList", {
            name: athleteToChange.name,
            listLabel: referAsClients
              ? i18n.t("auth:user.clientLabel")
              : i18n.t("auth:user.athleteLabel"),
          })
        );
        res.redirect("/coaching-portal/");
      }
    )
    // A coach updates the profile they have on an athlete
    .post("/user/update-athlete-profile/:athleteId", async (attribute) => {
      const {
        req: { body, params },
        res,
        locals,
        api,
        flash,
        assert,
        load,
      } = attribute;

      assert(params.athleteId, 400, i18n.t("auth:user.specifyUserToUpdate2"));
      const { activeUser } = locals;

      const athleteId = params.athleteId;
      await load("athlete", { user: athleteId });

      const userToRequest =
        !!body.coachOrganizationInformation &&
        String(
          whoIsAthleteCoach(
            activeUser.athletes,
            athleteId,
            activeUser._id,
            body.coachOrganizationInformation._id
          )
        ) !== String(activeUser._id)
          ? body.coachOrganizationInformation
          : activeUser;

      delete body.coachOrganizationInformation;

      const athleteToChange = find(userToRequest.athletes, {
        _id: athleteId,
      });
      // Update athlete
      const booleanGoals = body.goals ? formatProfileItem(body.goals) : [];
      const booleanLimitations = body.limitations
        ? formatProfileItem(body.limitations)
        : [];
      const booleanEquipment = body.equipment
        ? formatProfileItem(body.equipment)
        : [];

      athleteToChange.goals = booleanGoals;
      athleteToChange.limitations = booleanLimitations;
      athleteToChange.equipment = booleanEquipment;
      athleteToChange.notes =
        body.notes === undefined ? athleteToChange.notes : body.notes;
      syncAthleteData(athleteToChange, locals.athlete);

      //Save and refresh
      await api(`update-user-athlete/${userToRequest._id}`, {
        method: "PUT",
        body: filterAthletesAttrs(athleteToChange),
      });

      flash("success", i18n.t("auth:user.updatedSuccess"));
      res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
    })
    .post("/user/generate-account-link", async ({ res, api }) => {
      const link = await (
        await api(`generate-account-links`, {
          method: "POST",
        })
      ).json();
      res.refreshWith(
        { link: link.accountLinks.url },
        { url: "back", hash: "#_" }
      );
    })
    .post("/user/finish-account-link", async ({ res, api }) => {
      await api(`finished-account-links`, {
        method: "POST",
      });
      res.refreshWith({}, { url: "/coaching-portal/store", hash: "#_" });
    })
    .post(
      "/user/update-video-upload-permission",
      async ({ req: { body }, res, api, flash }) => {
        await api(`user/${body.userId}`, {
          method: "PUT",
          body: {
            videoUploadForbidden: Boolean(Number(body.videoUploadForbidden)),
          },
        });
        flash("success", i18n.t("auth:user.permissionUpdated"));
        res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
      }
    )
    .post(
      "/user/update-custom-mobile-icon-permission",
      async ({ req: { body }, res, api, flash }) => {
        await api(`user/${body.userId}`, {
          method: "PUT",
          body: {
            customMobileIcon: Boolean(Number(body.customMobileIcon)),
          },
        });
        flash("success", i18n.t("auth:user.customMobileIconUpdated"));
        res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
      }
    )
    .post(
      "/user/update-custom-appstore-listing-permission",
      async ({ req: { body }, res, api, flash }) => {
        await api(`user/${body.userId}`, {
          method: "PUT",
          body: {
            appStoreListingEnable: Boolean(Number(body.appStoreListingEnable)),
          },
        });
        flash("success", i18n.t("auth:user.appStoreListingUpdated"));
        res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
      }
    )
    .post(
      "/user/update-referral-program",
      async ({ req: { body }, res, api, flash }) => {
        const { purpose } = body;
        const message =
          purpose === "hide-link"
            ? i18n.t("auth:user.referralLinkHidden")
            : i18n.t("auth:user.referralLinkUpdated");
        delete body.purpose;

        await api(`referral-link-info`, {
          method: "PUT",
          body,
        });
        flash("success", message);
        res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
      }
    )
    .post("/user/enable-chat", async ({ req: { body }, res, api, flash }) => {
      await api(`user/${body.userId}`, {
        method: "PUT",
        body: {
          chatEnabled: true,
        },
      });
      flash("success", i18n.t("auth:user.chatEnabledSuccess"));
      res.refreshWith(
        { modal: "" },
        { url: "/coaching-portal/chat", hash: "#_" }
      );
    })
    .post("/user/remove-coach", async ({ req: { body }, res, api, flash }) => {
      await api("manage-coaching", {
        method: "PUT",
        body: {
          actionToPerform: "remove",
          athleteId: body.athleteId,
        },
      });
      flash("success", i18n.t("auth:user.coachRemovedSuccess"));
      res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
    })
    // Add exercises from the catalog to the users library.
    .post(
      "/user/catalog-add-exercises",
      async ({ req, res, api, locals, assert, flash }) => {
        const { activeUserToRequest, activeUser } = locals;
        const { selectedExercises, onboarding = false } = req.body;
        // defaulting to empty arrays because rill is making these undefined if the component sends empty arrays
        try {
          if (onboarding && activeUser?._id !== activeUserToRequest?._id) {
            assert(false, 400, i18n.t("auth:user.noPermission"));
          }
          const {
            squat = [],
            bench = [],
            deadlift = [],
            assistant = [],
            clean = [],
            jerk = [],
            snatch = [],
            overheadPress = [],
            other = [],
          } = selectedExercises;

          assert(
            squat.length ||
              bench.length ||
              deadlift.length ||
              assistant.length ||
              clean.length ||
              jerk.length ||
              snatch.length ||
              overheadPress.length ||
              other.length,
            400,
            i18n.t("auth:user.selectOneExercise")
          );

          // Only the newly selected exercises are sent — msb-api merges
          // them into whichever categories already exist atomically, so a
          // coach with a large library never resends the whole thing.
          const exercisesByPrimary = {
            squat: buildLibraryArray(squat, 100),
            bench: buildLibraryArray(bench, 100),
            deadlift: buildLibraryArray(deadlift, 100),
            assistant: buildLibraryArray(assistant),
            clean: buildLibraryArray(clean, 100),
            jerk: buildLibraryArray(jerk, 100),
            snatch: buildLibraryArray(snatch, 100),
            overheadPress: buildLibraryArray(overheadPress, 100),
            other: buildLibraryArray(other, 100),
          };

          await api(`exercise-library-catalog-add/${activeUserToRequest._id}`, {
            method: "POST",
            body: { exercisesByPrimary },
          });

          if (onboarding) {
            flash("success", i18n.t("auth:user.accountSetupSuccess"));
            return res.redirect("/coaching-portal");
          }

          flash("success", i18n.t("auth:user.exercisesAddedSuccess"));

          res.redirect("/coaching-portal/exercise-library");
        } catch (error) {
          if (onboarding && activeUser?._id !== activeUserToRequest?._id) {
            console.error("Error: ", error?.message);
            console.error(
              "Trying to re-signup a coach part of an organization"
            );
            res.refreshWith(
              {
                flashError: i18n.t("auth:user.reSignupCoachOrg"),
              },
              { url: "/logout", hash: "#_" }
            );
          } else {
            console.error("Error setting up account", error?.message);
          }
        }
      }
    )

    .post(
      "/user/check-chat-users/:athleteId/:calledFrom",
      async ({ req: { params }, res, api, flash }) => {
        const { calledFrom = "pull", athleteId = null } = params;
        const response = await (
          await api(
            `check-conversation?id=${params.athleteId}&calledFrom=${calledFrom}`
          )
        ).json();
        const { conversation, chatUserExist } = response;
        if (chatUserExist) {
          const isCommunity = ["push", "press", "perform", "pull"].includes(
            calledFrom
          );
          const url = isCommunity
            ? `/coaching-portal/community-chat?initId=${encodeURIComponent(conversation)}`
            : `/coaching-portal/chat?initId=${athleteId}`;

          res.redirect(url);
        } else {
          flash("error", i18n.t("auth:user.checkUserError"));
          res.backDontScroll();
        }
      }
    )

    .post(
      "/user/press-custom-branding/gym-location",
      async ({ req, res, flash, api, locals }) => {
        const { body } = req;
        const { bodyformatting = [] } = body;
        const { activeUser } = locals;
        const { pressCustomBranding = {} } = activeUser;

        const newBody = {};

        newBody.pressCustomBranding = { ...pressCustomBranding };
        newBody.pressCustomBranding.gymLocations = bodyformatting;

        try {
          await api(`user/${activeUser._id}`, {
            method: "PUT",
            body: newBody,
          });
          flash("success", i18n.t("auth:user.gymLocationDeletedSuccess"));
        } catch (e) {
          flash("error", e.message);
        }

        res.redirect("/coaching-portal/settings/gym-locations");
      }
    )
    .post(
      "/user/cancel-subscriber",
      async ({ api, assert, flash, req, res, locals }) => {
        const { body } = req;
        assert(body.customer, 400, i18n.t("auth:user.customerIdRequired"));
        assert(
          body.type === "membership" || body.type === "subscription",
          400,
          i18n.t("auth:user.productTypeRequired")
        );
        let bodyToSend = body;
        if (locals.activeUser.organizationId) {
          bodyToSend = {
            ...body,
            organizationUser: locals.activeUserToRequest._id,
          };
        }
        try {
          await api("cancel-subscriber", {
            method: "PUT",
            body: bodyToSend,
          });
          flash("success", i18n.t("auth:user.successfullyCancelled"));
          res.refreshWith({ customer: "" }, { url: "back", hash: "#_" });
        } catch (e) {
          flash("error", e.message);
          res.refreshWith({ customer: "" }, { url: "back", hash: "#_" });
        }
      }
    )
    .post("/user/charge-invoice", async ({ api, assert, flash, req, res }) => {
      const { invoiceId } = req.body;
      assert(invoiceId, 400, i18n.t("auth:user.invoiceIdRequired"));
      // The API derives the connected account from the session (own or
      // org owner's), so there's no organizationUser to pass here.
      try {
        await api("fe-invoice-pay", {
          method: "POST",
          body: { invoiceId },
        });
        flash("success", i18n.t("auth:user.chargeSucceeded"));
      } catch (e) {
        flash("error", e.message);
      }
      res.backDontScroll();
    })
    .post(
      "/user/change-membership",
      async ({ api, assert, flash, req, res }) => {
        const { body } = req;
        assert(body.customer, 400, i18n.t("auth:user.customerIdRequired"));
        assert(body.price, 400, i18n.t("auth:user.planRequired"));
        try {
          await api("manage-press-membership", {
            method: "PUT",
            body: {
              proxyUser: body.customer,
              plan: body.price,
            },
          });
          flash("success", i18n.t("auth:user.successfullyChanged"));
          res.refreshWith(
            { customer: "", price: "" },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.refreshWith(
            { customer: "", price: "" },
            { url: "back", hash: "#_" }
          );
        }
      }
    )
    .post(
      "/user/send-custom-email",
      async ({ api, assert, flash, req, res }) => {
        const { body } = req;
        assert(body.type, 400, i18n.t("auth:user.typeRequired"));
        try {
          await api("send-custom-email", {
            method: "POST",
            body,
          });
          flash("success", i18n.t("auth:user.checkEmailCoachApp"));
        } catch (e) {
          flash("error", e.message);
        }
        res.backDontScroll();
      }
    )
    .post(
      "/user/enable-community-chat",
      async ({ api, assert, flash, res, locals }) => {
        const { activeUserToRequest } = locals;
        const {
          _id: userId,
          pushAccountInfo,
          pressAccountInfo,
          performSettings,
          pullAccountInfo,
        } = activeUserToRequest;
        assert(userId, 400, i18n.t("auth:user.userIdRequired"));

        const isValidAccountInfo = (obj) =>
          obj && typeof obj === "object" && Object.keys(obj).length > 0;

        const body = {};

        if (isValidAccountInfo(pushAccountInfo)) {
          body.pushAccountInfo = {
            ...pushAccountInfo,
            inAppCommunityEnabled: true,
          };
        }
        if (isValidAccountInfo(pressAccountInfo)) {
          body.pressAccountInfo = {
            ...pressAccountInfo,
            inAppCommunityEnabled: true,
          };
        }
        if (isValidAccountInfo(performSettings)) {
          body.performSettings = {
            ...performSettings,
            inAppCommunityEnabled: true,
          };
        }
        if (pullAccountInfo && typeof pullAccountInfo === "object") {
          body.pullAccountInfo = {
            ...pullAccountInfo,
            inAppCommunityEnabled: true,
          };
        }

        assert(
          Object.keys(body).length > 0,
          400,
          i18n.t("auth:user.userMustBePush")
        );

        try {
          await api(`user/${userId}`, {
            method: "PUT",
            body,
          });
          flash("success", i18n.t("auth:user.communityChatEnabled"));
          res.backDontScroll();
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/user/facilikey-resync",
      async ({ api, assert, flash, req, res }) => {
        const { body } = req;
        assert(body.customer, 400, i18n.t("auth:user.customerIdRequired"));
        try {
          await api("facilikey-resync", {
            method: "POST",
            body: {
              proxyUser: body.customer,
            },
          });
          flash("success", i18n.t("auth:user.successfullyResynced"));
          res.refreshWith({ customer: "" }, { url: "back", hash: "#_" });
        } catch (e) {
          flash("error", e.message);
          res.refreshWith({ customer: "" }, { url: "back", hash: "#_" });
        }
      }
    )
    .post(
      "/user/edit-meal-plan-data-source-preference",
      async ({ req: { body }, res, api, flash }) => {
        try {
          await api(`meal-plan-data-source-preference`, {
            method: "PUT",
            body,
          });
          flash("success", i18n.t("auth:user.userPrefsUpdated"));
          res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
        } catch (error) {
          flash("error", getErrorMessage(error));
          res.backDontScroll();
        }
      }
    )
    .post(
      "/user/meal-plan-data-source-preference",
      async ({ req: { body }, res, api }) => {
        res.headers = res.headers || {};
        res.headers["Content-Type"] = "application/json; charset=utf-8";

        let parsed = body;
        if (typeof body === "string") {
          try {
            parsed = JSON.parse(body);
          } catch {
            parsed = null;
          }
        }
        const { userId, mealPlanDataSources } = parsed || {};
        if (!userId || !Array.isArray(mealPlanDataSources)) {
          res.status = 400;
          res.body = JSON.stringify({ error: i18n.t("common:errors.default") });
          return;
        }

        try {
          const response = await api(`meal-plan-data-source-preference`, {
            method: "PUT",
            body: {
              stringBody: JSON.stringify({ userId, mealPlanDataSources }),
            },
          });
          const doc = await response.json().catch(() => null);
          res.status = 200;
          res.body = JSON.stringify({
            mealPlanDataSources:
              doc?.mealPlanDataSources ?? mealPlanDataSources,
            message: i18n.t("auth:user.userPrefsUpdated"),
          });
        } catch (error) {
          const status = error?.status ?? error?.code;
          res.status = Number.isInteger(status) && status >= 400 ? status : 500;
          res.body = JSON.stringify({ error: getErrorMessage(error) });
        }
      }
    )
    .post(
      "/user/activate-perform",
      async ({ req: { body }, res, api, flash, assert }) => {
        assert(body.userId, 400, "User ID is required");
        try {
          await api(`stripe-create-perform-subscription?user=${body.userId}`, {
            method: "PUT",
            body: {},
          });
          flash("success", "PERFORM subscription activated");
          res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
        } catch (e) {
          flash("error", e.message);
          res.refreshWith({}, { url: "back", hash: "#_" });
        }
      }
    )
    .post(
      "/user/cancel-perform",
      async ({ req: { body }, res, api, flash, assert }) => {
        assert(body.userId, 400, "User ID is required");
        try {
          await api(`stripe-cancel-perform-subscription?user=${body.userId}`, {
            method: "PUT",
            body: {},
          });
          flash("success", "PERFORM subscription cancelled");
          res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
        } catch (e) {
          flash("error", e.message);
          res.refreshWith({}, { url: "back", hash: "#_" });
        }
      }
    )
    .post(
      "/user/request-meal-plan-access",
      async ({ api, assert, flash, req, res }) => {
        try {
          const { body } = req;
          assert(
            body.password,
            400,
            i18n.t("auth:user.aiPlannerPasswordRequired")
          );

          await api("meal-plan-request-access", {
            method: "PUT",
            body,
          });

          flash("success", i18n.t("auth:user.mealPlannerAccessGranted"));
          res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
        } catch (error) {
          flash("error", getErrorMessage(error));
          res.backDontScroll();
        }
      }
    );
};

export default User;
